import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Star, LogOut, Award, Package, TrendingUp, Zap, Flame, Crown, Share2, Gift, Target, Bell, BellOff, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import ShareCard from "@/components/ShareCard";
import { MONETIZATION_ENABLED } from "@/lib/features";
import { pushSupported, currentPushStatus, enablePush, disablePush } from "@/lib/push";
import { toast } from "sonner";

const BADGES = [
  { min: 0, label: "Iniciante", color: "text-zinc-400" },
  { min: 5, label: "Bronze", color: "text-orange-400" },
  { min: 20, label: "Prata", color: "text-zinc-300" },
  { min: 50, label: "Ouro", color: "text-amber-400" },
  { min: 100, label: "Diamante", color: "text-cyan-400" },
];

function badgeFor(sales) {
  return [...BADGES].reverse().find((b) => sales >= b.min) || BADGES[0];
}

export default function Profile() {
  const { user, logout } = useAuth();
  const [shareOpen, setShareOpen] = useState(false);
  const [pushState, setPushState] = useState({ supported: false, subscribed: false, permission: "default" });
  const [pushBusy, setPushBusy] = useState(false);
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    let mounted = true;
    if (pushSupported()) {
      currentPushStatus().then((s) => { if (mounted) setPushState(s); });
    }
    return () => { mounted = false; };
  }, []);

  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushState.subscribed) {
        await disablePush();
        toast.success("Notificações desativadas");
      } else {
        await enablePush();
        toast.success("Notificações ativadas — vamos avisar quando algo combinar");
      }
      const s = await currentPushStatus();
      setPushState(s);
    } catch (e) {
      toast.error(e?.message || "Falha ao configurar notificações");
    } finally {
      setPushBusy(false);
    }
  };

  if (!user) return null;
  const badge = badgeFor(user.total_sales || 0);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
      <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center font-heading font-black text-2xl">
            {user.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-heading font-black text-2xl tracking-tight truncate">
              {user.name}
            </h1>
            <p className="text-sm text-muted-foreground truncate">{user.email}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-xs font-bold ${badge.color} flex items-center gap-1`}>
                <Award size={12} /> {badge.label}
              </span>
              <span className="text-xs text-muted-foreground capitalize">· {user.role}</span>
              {user.doc_type && <span className="text-xs text-muted-foreground uppercase">· {user.doc_type}</span>}
              {user.is_fast_seller && (
                <span
                  data-testid="profile-fast-badge"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded-full"
                >
                  <Zap size={10} strokeWidth={3} /> Vendedor Rápido
                </span>
              )}
              {user.doc_verified && (
                <span
                  data-testid="profile-verified-badge"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/40 px-2 py-0.5 rounded-full"
                  title="Documento verificado"
                >
                  <ShieldCheck size={10} strokeWidth={3} /> Verificado
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-6">
          <div className="bg-secondary rounded-xl p-4 text-center">
            <Star size={16} className="text-amber-500 mx-auto mb-1" />
            <p className="font-heading font-black text-2xl">
              {user.rating_avg > 0 ? user.rating_avg.toFixed(1) : "—"}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {user.rating_count} avaliações
            </p>
          </div>
          <div className="bg-secondary rounded-xl p-4 text-center">
            <Package size={16} className="text-primary mx-auto mb-1" />
            <p className="font-heading font-black text-2xl">{user.total_sales || 0}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Vendas</p>
          </div>
          <div className="bg-secondary rounded-xl p-4 text-center">
            <TrendingUp size={16} className="text-emerald-500 mx-auto mb-1" />
            <p className="font-heading font-black text-2xl">{user.total_purchases || 0}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Compras</p>
          </div>
        </div>

        {user.avg_response_seconds != null && (
          <div className="mt-3 bg-secondary rounded-xl p-4 flex items-center gap-3" data-testid="response-time-card">
            <Zap size={20} className="text-primary shrink-0" />
            <div className="flex-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
                Tempo médio de resposta
              </p>
              <p className="font-heading font-black text-2xl mt-0.5">
                {user.avg_response_seconds < 60
                  ? `${Math.round(user.avg_response_seconds)}s`
                  : `${Math.round(user.avg_response_seconds / 60)}min`}
                <span className="text-xs text-muted-foreground ml-2 font-normal">
                  em {user.response_count} pedidos
                </span>
              </p>
            </div>
          </div>
        )}

        {/* Streaks */}
        {(user.fast_accepts_this_week > 0 || user.top10_streak > 0) && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="streaks-block">
            {user.fast_accepts_this_week > 0 && (
              <div
                data-testid="streak-fast-accepts"
                className="bg-gradient-to-br from-orange-500/20 to-amber-500/10 border border-orange-500/30 rounded-xl p-4 flex items-center gap-3"
              >
                <Flame size={26} className="text-orange-400 shrink-0" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-orange-400 font-bold">
                    Streak semanal
                  </p>
                  <p className="font-heading font-black text-2xl mt-0.5">
                    {user.fast_accepts_this_week}
                    <span className="text-xs text-muted-foreground ml-2 font-normal">
                      pedidos aceitos em &lt;2min
                    </span>
                  </p>
                </div>
              </div>
            )}
            {user.top10_streak > 0 && (
              <div
                data-testid="streak-top10"
                className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded-xl p-4 flex items-center gap-3"
              >
                <Crown size={26} className="text-primary shrink-0" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-primary font-bold">
                    Top 10 semanal
                  </p>
                  <p className="font-heading font-black text-2xl mt-0.5">
                    {user.top10_streak}
                    <span className="text-xs text-muted-foreground ml-2 font-normal">
                      {user.top10_streak === 1 ? "semana seguida" : "semanas seguidas"}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 space-y-2 text-sm">
          <div className="flex justify-between border-b border-border py-2">
            <span className="text-muted-foreground">Cidade</span>
            <span className="font-medium">{user.city || "—"}</span>
          </div>
          <div className="flex justify-between border-b border-border py-2">
            <span className="text-muted-foreground">Cadastro</span>
            <span className="font-medium">{user.doc_type?.toUpperCase() || "—"}</span>
          </div>
        </div>

        <Button
          onClick={() => setShareOpen(true)}
          data-testid="profile-share-btn"
          className="w-full mt-6 gap-2 h-11 font-bold"
        >
          <Share2 size={16} /> Compartilhar meu perfil
        </Button>

        {pushState.supported && (
          <Button
            onClick={togglePush}
            data-testid="profile-push-toggle"
            variant="outline"
            disabled={pushBusy}
            className="w-full mt-2 gap-2 h-11"
          >
            {pushState.subscribed ? <BellOff size={16} /> : <Bell size={16} />}
            {pushState.subscribed ? "Desativar notificações" : "Ativar notificações"}
          </Button>
        )}

        <Link to="/especialidades" data-testid="profile-preferences-link">
          <Button variant="outline" className="w-full mt-2 gap-2 h-11">
            <Target size={16} /> Minhas Especialidades
          </Button>
        </Link>

        {MONETIZATION_ENABLED && (
          <Link to="/pagamentos" data-testid="profile-payments-link">
            <Button variant="outline" className="w-full mt-2 gap-2 h-11">
              <Zap size={16} /> Créditos e Premium
            </Button>
          </Link>
        )}

        <Link to="/convites" data-testid="profile-referrals-link">
          <Button variant="outline" className="w-full mt-2 gap-2 h-11">
            <Gift size={16} /> Meus convites
            {user.total_referrals > 0 && (
              <span className="ml-auto bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {user.total_referrals}
              </span>
            )}
          </Button>
        </Link>

        <Button
          onClick={logout}
          data-testid="profile-logout"
          variant="outline"
          className="w-full mt-2 gap-2"
        >
          <LogOut size={16} /> Sair da conta
        </Button>
      </div>
      <ShareCard open={shareOpen} onClose={() => setShareOpen(false)} user={user} appUrl={appUrl} />
    </div>
  );
}
